import { Injectable, OnDestroy } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { concatMap, debounceTime, takeUntil } from 'rxjs/operators';
import { constants } from '../../../constants';
import { AgeGroup } from '../models/age-group.model';
import {
  NameValuePair,
  ToggleableNameValuePair,
} from '../models/name-value-pair.model';
import { RegionSelection } from '../models/region-selection.model';
import { SpendingGroup } from '../models/spending-group.model';
import { AuthService } from '../../auth/services/auth.service';
import { Category } from '../models/category.model';
import { IncomeGroup } from '../models/income-group.model';
import { flatCategories } from '../../../utils';

export class DataFilters {
  public regionSelection: RegionSelection = new RegionSelection();
  public gender: 'male' | 'female' | undefined = undefined;
  public generation: string | undefined = undefined;
  public ageGroup: AgeGroup = new AgeGroup();
  public spendingGroup: SpendingGroup | IncomeGroup = new SpendingGroup();
  public year: number = constants.initialYear;
  public currency: number = constants.data.currencyView[0].value;
  public category: string = '0'; // Total Spending Value
  public freemiumCategory: Category = {
    code: undefined,
    label: 'All categories',
  };
  public spendingPer: number = constants.data.spendingPer[0].value;
  public dataLens: number = constants.data.dataLensOptions[0].value;
  public dashboard: string = constants.data.systemDashboards[0];

  public static getCategoriesValue(
    categories: ToggleableNameValuePair[],
    value: any
  ) {
    const flattenedCategories = flatCategories(categories);
    return (
      flattenedCategories.find((pair) => pair.value === value) ||
      flattenedCategories[0]
    );
  }

  public static getNameValuePair(nameValuePairs: NameValuePair[], value: any) {
    return (
      nameValuePairs.find((pair) => pair.value === value) || nameValuePairs[0]
    );
  }

  public static copy(source: DataFilters, target: DataFilters) {
    Object.assign(target, source);
    target.regionSelection = new RegionSelection();
    RegionSelection.copy(source.regionSelection, target.regionSelection);
  }
}

@Injectable({
  providedIn: 'root',
})
export class DataFiltersService implements OnDestroy {
  private dataFilters: BehaviorSubject<DataFilters> =
    new BehaviorSubject<DataFilters>(new DataFilters());
  private changedDataFilters: Subject<Partial<DataFilters>> = new Subject();
  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    private gtmService: GoogleTagManagerService,
    private authService: AuthService
  ) {
    this.changedDataFilters
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(500),
        concatMap((dataFilters) => {
          // telemetry
          let filterChanged = Object.keys(dataFilters)[0];
          let filterValue;
          switch (filterChanged) {
            case 'regionSelection':
              filterValue = JSON.stringify({
                regions: dataFilters.regionSelection?.getRegionCodes(),
                countries: dataFilters.regionSelection?.getCountryCodes(),
                subnational: dataFilters.regionSelection?.getSubnationalCodes(),
              });
              break;
            case 'gender':
              filterValue = dataFilters.gender ? dataFilters.gender : 'All';
              break;
            case 'ageGroup':
              filterValue = dataFilters.ageGroup?.displayValue();
              break;
            case 'spendingGroup':
              filterValue = dataFilters.spendingGroup?.displayValue();
              break;
            case 'year':
              filterValue = dataFilters.year;
              break;
            case 'currency':
              filterValue = dataFilters.currency
                ? 'Nominal Dolars'
                : '2021 PPP USD';
              break;
            case 'spendingPer':
              filterValue = dataFilters.spendingPer ? 'Year' : 'Day';
              break;
            case 'dataLens':
              filterValue = dataFilters.dataLens ? 'Subnational' : 'National';
              break;
            case 'dashboard':
              filterValue = dataFilters.dashboard;
              break;
            default:
              filterValue = 'unknown';
              break;
          }
          const gtmTag = {
            event: 'filterChange',
            filterChanged,
            filterValue,
            isAllowed: true,
          };

          return of(this.gtmService.pushTag(gtmTag));
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public patchDataFilters(dataFilters: Partial<DataFilters>): void {
    this.dataFilters.next({ ...this.dataFilters.getValue(), ...dataFilters });
    this.changedDataFilters.next({ ...dataFilters });
  }

  public setDataFilters(dataFilters: DataFilters): void {
    this.dataFilters.next({ ...dataFilters });
    this.changedDataFilters.next({ ...dataFilters });
  }

  public getDataFilters(): Observable<DataFilters> {
    return this.dataFilters.asObservable();
  }

  public getDataFiltersValue(): DataFilters {
    return this.dataFilters.getValue();
  }

  public valueChanges(): Observable<Partial<DataFilters>> {
    return this.changedDataFilters.asObservable();
  }

  public resetAll() {
    this.setDataFilters({
      ...new DataFilters(),
    });
  }

  public resetLocation() {
    this.patchDataFilters({
      regionSelection: new RegionSelection(),
      dataLens: 0,
    });
  }

  public resetCategories() {
    this.patchDataFilters({
      category: '0',
    });
  }

  public resetGender() {
    this.patchDataFilters({
      gender: undefined,
    });
  }

  public resetCity() {
    this.patchDataFilters({
      regionSelection: new RegionSelection(
        new Map(),
        this.getDataFiltersValue().regionSelection.countries
      ),
    });
  }
}
